// src/components/CreatePool.js
import React, { useState } from "react";
import { ethers } from "ethers";
import contractABI from "../contractABI.json";
import { CONTRACT_ADDRESS, USDT_ADDRESS } from "../utils";
import Modal from "./Modal";
import '../App.css';

export default function CreatePool({ wallet, USDTAddress, level, investment, onPoolCreated }) {
  const [showModal, setShowModal] = useState(false);
  const [requiredAmount, setRequiredAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contract, setContract] = useState(null);

  const LEVEL_NAMES = {
    0: "Entry",
    1: "Stacker",
    2: "Scaler",
    3: "Flipper",
    4: "Holder",
    5: "Broker",
    6: "Tycoon",
    7: "Legend",
  };

  const [alertModal, setAlertModal] = useState({ show: false, title: "", message: "" });

  const showAlert = (title, message) => {
    setAlertModal({ show: true, title, message });
  };

  const handleCreatePool = async () => {
    try {
      setLoading(true);
      const poolContract = new ethers.Contract(CONTRACT_ADDRESS, contractABI, wallet.signer);
      setContract(poolContract);

      const usdtContract = new ethers.Contract(
        USDTAddress,
        ["function allowance(address owner, address spender) view returns (uint256)"],
        wallet.provider
      );

      const amount = ethers.parseUnits(investment.toString(), 18);
      setRequiredAmount(amount);

      const allowance = await usdtContract.allowance(wallet.address, CONTRACT_ADDRESS);

      if (allowance < amount) {
        setShowModal(true);
        return;
      }

      const tx = await poolContract.createPool(level);
      await tx.wait();

      showAlert("Success", `Pool ${LEVEL_NAMES[level]} created successfully!`);
    } catch (error) {
      console.error("Error creating pool:", error);
      const message =
        error?.reason ||
        error?.error?.message ||
        error?.data?.message ||
        error?.message ||
        "An unexpected error occurred while creating the pool.";

      showAlert("Error", `Failed to create pool:\n${message}`);
    } finally {
      setLoading(false);
      if (onPoolCreated) onPoolCreated();
    }
  };

  const handleApproveAndContinue = async () => {
    try {
      setLoading(true);
      const usdtContract = new ethers.Contract(
        USDTAddress,
        ["function approve(address spender, uint256 amount) public returns (bool)"],
        wallet.signer
      );

      const tx = await usdtContract.approve(CONTRACT_ADDRESS, requiredAmount);
      await tx.wait();

      setShowModal(false);
      const createTx = await contract.createPool(level);
      const receipt = await createTx.wait();

      const poolCreatedEvent = receipt.events && receipt.events.find(
        (event) => event.event === "PoolCreated"
      );
      let createdPoolName = LEVEL_NAMES[level] || `Level ${level}`;
      if (poolCreatedEvent) {
        const poolLevel = poolCreatedEvent.args.level;
        createdPoolName = LEVEL_NAMES[poolLevel] || `Level ${poolLevel}`;
      }
      showAlert("Success", `New ${createdPoolName} Pool created successfully!`);
    } catch (error) {
      console.error("Approval or pool creation failed:", error);
      showAlert("Error", "Transaction failed.");
    } finally {
      setLoading(false);
      if (onPoolCreated) onPoolCreated();
    }
  };

  return (
    <div>
      <button
        onClick={handleCreatePool}
        className={`create-pool-btn ${loading ? "disabled" : ""}`}
        disabled={loading}
      >
        {loading ? <span className="loader mr-2" /> : "Create Pool"}
      </button>
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="p-4 text-center">
            <h3 className="text-lg font-bold mb-2">Approve USDT</h3>
            <p className="text-sm mb-4 whitespace-pre-wrap">
              Your current allowance is insufficient.<br />
              Do you want to approve <strong>{investment} USDT</strong> to proceed?
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <button onClick={() => setShowModal(false)} className="btn-cancel px-4 py-2">Cancel</button>
              <button onClick={handleApproveAndContinue} disabled={loading} className="btn-primary px-4 py-2">
                {loading ? "Approving..." : "Approve & Continue"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {alertModal.show && (
        <Modal isOpen={alertModal.show} onClose={() => setAlertModal({ show: false, title: "", message: "" })}>
          <div className="p-4 text-center">
            <h3 className="text-xl font-bold mb-3 text-gray-800 dark:text-gray-100">
              {alertModal.title}
            </h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-6">
              {alertModal.message}
            </p>
            <button className="btn-primary px-4 py-2" onClick={() => setAlertModal({ show: false, title: "", message: "" })}>
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
